import type { CatalogProductImage } from "@shared/types";

/**
 * Whether two image lists are the same, field by field.
 *
 * EVERY FIELD, not just the URL. Both callers use this to decide whether to
 * send `images` at all, so a comparison that only looked at URLs would silently
 * discard an operator's alt text: same URLs in the same order reads as
 * "unchanged", and the patch is dropped with the description in it.
 *
 * Before the column carried alt and type there was nothing else to compare,
 * which is why the URL check used to be the whole of it.
 *
 * ORDER COUNTS, because order is what makes an image primary. Two lists holding
 * the same pictures in a different sequence are a different product.
 */
export function sameImages(
	a: CatalogProductImage[],
	b: CatalogProductImage[],
): boolean {
	return (
		a.length === b.length &&
		a.every(
			(image, index) =>
				image.url === b[index]?.url &&
				image.type === b[index]?.type &&
				image.alt === b[index]?.alt,
		)
	);
}

const APP_CATALOG_STORAGE_PATH = "/api/v1/storage/catalog/";
const ADMIN_CATALOG_STORAGE_PATH = "/api/v1/admin/storage/catalog/";

/**
 * The URL the console renders a stored catalogue image from.
 *
 * An image stored on R2 is saved as a URL on the app's storage proxy,
 * `/api/v1/storage/catalog/...`, which reads only the Finishd app's session.
 * The console holds the admin session, so it loads the same file from the
 * admin copy of that proxy (#457). Any other URL (a local `/uploads/...` path
 * or a third-party image) is returned unchanged. Only for rendering: the
 * product keeps the stored URL.
 */
export function catalogImageSrc(url: string): string {
	return url.replace(APP_CATALOG_STORAGE_PATH, ADMIN_CATALOG_STORAGE_PATH);
}
